<template>
  <c-sidesheet id="inventory-type-modal" :title="title" :active="active" @close="close">
    <div v-if="loading">Loading...</div>

    <div v-else class="inventory-type">
      <div class="inventory-type__form">
        <div class="row">
          <div class="col col-xl-6">
            <div
              class="form-group inventory-type__name"
              :class="{
                'is-invalid': isSubmitted && nameError,
              }"
            >
              <c-input
                v-model="form.name"
                floating-label="Наименование"
                :clearable="true"
                :max-length="500"
                :required-mark="true"
                :required="isSubmitted && nameError"
                :required-message="isSubmitted && nameError"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-6">
            <div
              class="form-group inventory-type__category"
              :class="{
                'is-invalid': isSubmitted && parentError,
              }"
            >
              <c-input
                v-model="form.parentId"
                type="select"
                :items="catalogList"
                floating-label="Категория/подкатегория"
                :clearable="true"
                :searchable="true"
                :with-categorized="true"
                :required-mark="true"
                :required="isSubmitted && parentError"
                :required-condition="isSubmitted && parentError"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-6">
            <div class="form-group inventory-type__producer">
              <c-input v-model="form.producer" floating-label="Производитель" :clearable="true" :max-length="255" />
            </div>
          </div>
          <div class="col col-xl-4">
            <div class="form-group inventory-type__model">
              <c-input v-model="form.model" floating-label="Модель" :clearable="true" :max-length="255" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-6">
            <div class="form-group inventory-type__responsible">
              <c-input
                v-model="form.responsible"
                type="select"
                :items="workersList"
                floating-label="Ответственный"
                :clearable="true"
                :searchable="true"
                :with-categorized="true"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-10">
            <div class="form-group inventory-type__description">
              <c-textarea v-model="form.description" :clearable="true" floating-label="Описание" max-length="500" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col col-xl-10">
            <div
              class="form-group inventory-type__link"
              :class="{
                'is-invalid': isSubmitted && linkError,
              }"
            >
              <c-input
                v-model="form.link"
                floating-label="Ссылка"
                :clearable="true"
                :max-length="500"
                :required="isSubmitted && linkError"
                :required-message="isSubmitted && linkError"
              />
            </div>
          </div>
        </div>

        <div class="form-group inventory-type__files">
          <file-loader
            ref="loader"
            v-model="files"
            format="images,pdf,docs,video"
            actions="loadFiles,preview"
            :links="fileLinks"
            :limit="10"
            @changeLinks="fileLinks = $event"
          >
            <template #note>
              <div class="note">
                Допустимые форматы изображений: .jpeg, .jpg, .gif, .png, видео: .mp4, .avi, .wmv, .mov, .3gp,
                .flv, .mpeg-1,2, .webm., документов: .doc, .docx, .pdf. Размер файла — не более 10Мб.
              </div>
            </template>
          </file-loader>
        </div>
      </div>

      <div
        class="inventory-type__actions"
        :class="{
          fixed: fixFooter,
        }"
      >
        <c-button class="inventory-type__reset" label="Отменить" size="large" @click="close" />
        <c-button
          class="inventory-type__save"
          label="Сохранить"
          color="primary"
          size="large"
          :disabled="pending"
          :pending="pending"
          @click="submit"
        />
        <observer
          ref="observer"
          class="footer-observer"
          @fullyVisible="fixFooter = false"
          @fullyHidden="fixFooter = true"
        />
      </div>
    </div>

    <template #actions>
      <c-button label="Отменить" size="large" @click="close" />
      <c-button label="Сохранить" color="primary" size="large" :disabled="pending" :pending="pending" @click="submit" />
    </template>
  </c-sidesheet>
</template>

<script>
import CSidesheet from "@/components/scrollable-sidesheet";
import CButton from "@/components/controls2/Button";
import CInput from "@/components/controls2/Input";
import CTextarea from "@/components/controls/textarea";
import FileLoader from "@/components/file-loader";
import Observer from "@/components/IntersectionObserver";

import { isURL } from "@/utils/validate/url";
import { REQUIRED_ERROR } from "@/constants/errorFieldValues";
import { formatTypeModel, TypeModel } from "../models/TypeModel";
import { CREATE_TYPE_ACTION, GET_CATEGORIES_ACTION, GET_TYPE_ACTION, UPDATE_TYPE_ACTION } from "../api";
import { Catalog } from "../models/Catalog";

export default {
  components: {
    CSidesheet,
    CButton,
    CInput,
    CTextarea,
    FileLoader,
    Observer,
  },

  data() {
    return {
      active: false,
      isSubmitted: false,
      pending: false,
      loading: true,

      id: null,
      type: null,
      catalog: Catalog(),

      form: {
        name: "",
        parentId: null,
        producer: "",
        model: "",
        responsible: null,
        description: "",
        link: "",
      },

      files: [],
      fileLinks: [],

      fixFooter: false,
    };
  },

  computed: {
    title() {
      return this.id ? "Редактировать тип ТМЦ" : "Новый тип ТМЦ";
    },

    catalogList() {
      const list = [];
      this.catalog.list.forEach((category) => {
        const { name: categoryName, id: categoryId, subCategories } = category;
        list.push({
          label: categoryName,
          formatLabel: `<b>${categoryName}</b>`,
          value: categoryId,
          isSubCategory: false,
        });

        subCategories.forEach((subCategory) => {
          const { name, id } = subCategory;
          list.push({
            label: name,
            formatLabel: `<b>${categoryName}</b>/${name}`,
            value: id,
            isSubCategory: true,
          });
        });
      });
      return list;
    },

    workersList() {
      return this.$store.getters["actors/workersList"];
    },

    nameError() {
      return this.form.name.trim() ? null : REQUIRED_ERROR;
    },

    parentError() {
      return this.form.parentId ? null : REQUIRED_ERROR;
    },

    linkError() {
      const link = this.form.link.trim();
      if (!link) return null;
      return isURL(link) ? null : "Неверный формат";
    },

    isValid() {
      return !this.nameError && !this.parentError && !this.linkError;
    },
  },

  watch: {
    loading(v) {
      if (!v && this.active) {
        this.$nextTick(() => {
          this.$refs.observer.refresh();
        });
      }
    },
  },

  created() {
    this.loadCategories();
  },

  methods: {
    loadCategories() {
      this.$store.dispatch(GET_CATEGORIES_ACTION, {}).then((res) => {
        this.catalog.update(res);
        this.loading = false;
      });
    },
    open(type) {
      this.loading = true;
      this.active = true;

      if (type) {
        this.setData(type);
      }

      this.loadCategories();
    },
    setData(type) {
      this.id = type.id;
      this.type = type;

      const { name, parentId, producer, model, responsible, descriptionModel } = type;
      const { description, link, files } = descriptionModel;
      this.form.name = name;
      this.form.parentId = parentId ? { value: parentId } : null;
      this.form.producer = producer;
      this.form.model = model;
      this.form.responsible = responsible ? { value: +responsible } : null;
      this.form.description = description;
      this.form.link = link;
      this.fileLinks = files;
    },
    close() {
      this.active = false;
      this.reset();
      this.$emit("close");
    },
    getParams() {
      const { name, parentId, producer, model, responsible, description, link } = this.form;

      const params = formatTypeModel({
        name,
        parentId: parentId && parentId.value,
        producer,
        model,
        responsible: responsible && responsible.value || '',
        description,
        link,
        files: this.files,
        oldFiles: this.fileLinks.map((file) => file.id),
      });

      if (this.id) {
        params.id = this.id;
      }

      return params;
    },
    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return;

      this.pending = true;
      const editMode = !!this.id;

      const action = editMode ? UPDATE_TYPE_ACTION : CREATE_TYPE_ACTION;
      const params = this.getParams();

      this.$store.dispatch(action, params).then((res) => {
        if (!res.success) {
          this.$notify({
            group: "error-message",
            data: {
              textMessage: "Произошла ошибка. Попробуйте еще раз",
            },
          });
          return;
        }
        this.$emit(editMode ? "updated" : "created", res.model);
        this.$notify({
          group: "success-message",
          data: {
            textMessage: editMode
              ? `Тип ТМЦ <b>${res.model.form_view}</b> успешно обновлен`
              : `Тип ТМЦ <b>${res.model.form_view}</b> успешно добавлен`,
          },
        });
        this.close();
      });
    },
    reset() {
      this.isSubmitted = false;
      this.pending = false;

      this.id = null;
      this.type = null;

      this.form.name = "";
      this.form.parentId = null;
      this.form.producer = "";
      this.form.model = "";
      this.form.responsible = null;
      this.form.description = "";
      this.form.link = "";

      this.files = [];
      this.filesLink = [];

      this.$refs.loader.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

.inventory-type {
  &__actions {
    display: none;
  }

  .note {
    font-size: 11px;
    display: flex;
    align-items: center;
    min-height: 30px;
    padding-top: 20px;
    color: $color-label;
    max-width: 600px;
  }
}

.is-invalid {
  padding-bottom: 24px;
}

@include bootstrapMd {
  .inventory-type {
    @include gridGenerator(11);

    .note {
      padding-top: 0;
      padding-left: 30px;
    }

    &__actions {
      display: flex;
      margin-top: 10px;
      padding-top: 14px;
      padding-bottom: 14px;
      position: sticky;
      bottom: -1px;

      &.fixed {
        background: white;
        z-index: 10;
        &:before {
          content: "";
          position: absolute;
          left: -30px;
          right: -40px;
          top: 0;
          bottom: 0;
          background: white;
          z-index: 0;
          box-shadow: 0px 0px 15px rgba(0, 21, 80, 0.3);
        }
      }

      .footer-observer {
        bottom: -1px;
        position: absolute;
      }
    }
    &__reset {
      margin-right: 10px;
    }
  }

  /deep/ {
    .modal-actions {
      display: none;
    }
  }
}
</style>
